import { useState } from "react";
import { useLazyQuery } from "@apollo/client";
import {
    RECENT_WAVE_EVENTS,
    WAVE_FLAG_HISTORY,
    WAVE_SESSIONS,
    WAVE_ALL_PARTICIPANT_ARMS,
} from "../queries/waves";

function toCSV(rows, columns) {
    const header = columns.join(",");
    const body = rows.map(r =>
        columns.map(c => JSON.stringify(r[c] ?? "")).join(",")
    ).join("\n");
    return header + "\n" + body;
}

function downloadCSV(filename, csv) {
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

function downloadJSON(filename, rows) {
    const blob = new Blob([JSON.stringify(rows, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

function ExportRow({ label, columns, filename, fetchQuery, variables, format }) {
    const [fetch, { loading, error }] = useLazyQuery(fetchQuery, {
        fetchPolicy: "network-only",
        onCompleted: (data) => {
            const key = Object.keys(data)[0];
            const rows = data[key];
            if (format === "json") downloadJSON(`${filename}.json`, rows);
            else downloadCSV(`${filename}.csv`, toCSV(rows, columns));
        },
    });

    return (
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #eee" }}>
            <span style={{ flex: 1, fontWeight: 500 }}>{label}</span>
            <span style={{ color: "#888", fontSize: 13 }}>{columns.join(", ")}</span>
            <button
                className="admin-btn"
                disabled={loading}
                onClick={() => fetch({ variables: { ...variables, limit: 10000 } })}
            >
                {loading ? "Fetching…" : "CSV"}
            </button>
            <button
                className="admin-btn"
                disabled={loading}
                onClick={() => fetch({ variables: { ...variables, limit: 10000 } })}
                data-format="json"
                onClickCapture={(e) => {
                    e.currentTarget.dataset.format = "json";
                }}
            >
                {loading ? "…" : "JSON"}
            </button>
            {error && <span style={{ color: "crimson", fontSize: 12 }}>{error.message}</span>}
        </div>
    );
}

export default function ExportTab({ waveId }) {
    const [eventsLoading, setEventsLoading] = useState(false);
    const [auditLoading, setAuditLoading] = useState(false);
    const [sessionsLoading, setSessionsLoading] = useState(false);
    const [armsLoading, setArmsLoading] = useState(false);

    const [fetchEvents] = useLazyQuery(RECENT_WAVE_EVENTS, { fetchPolicy: "network-only" });
    const [fetchAudit] = useLazyQuery(WAVE_FLAG_HISTORY, { fetchPolicy: "network-only" });
    const [fetchSessions] = useLazyQuery(WAVE_SESSIONS, { fetchPolicy: "network-only" });
    const [fetchArms] = useLazyQuery(WAVE_ALL_PARTICIPANT_ARMS, { fetchPolicy: "network-only" });

    const handle = (fetch, setLoading, dataKey, columns, filename, fmt, vars) => async () => {
        setLoading(true);
        try {
            const { data } = await fetch({ variables: vars });
            const rows = data?.[dataKey] ?? [];
            if (fmt === "json") downloadJSON(`wave${waveId}_${filename}.json`, rows);
            else downloadCSV(`wave${waveId}_${filename}.csv`, toCSV(rows, columns));
        } finally {
            setLoading(false);
        }
    };

    const EVENT_COLS = ["waveId", "type", "payload", "occurredAt"];
    const AUDIT_COLS = ["id", "flag_key", "old_value", "new_value", "changed_by", "changed_at"];
    const SESSION_COLS = ["session_id", "wave_id", "window_start", "window_end", "phase"];
    const ARM_COLS = ["participant_id", "session_id", "wave_id", "arm", "retained_or_reassigned"];

    const tables = [
        {
            label: "Event log",
            cols: EVENT_COLS,
            file: "events",
            loading: eventsLoading,
            csv: handle(fetchEvents, setEventsLoading, "recentWaveEvents", EVENT_COLS, "events", "csv", { waveId, limit: 10000 }),
            json: handle(fetchEvents, setEventsLoading, "recentWaveEvents", EVENT_COLS, "events", "json", { waveId, limit: 10000 }),
        },
        {
            label: "Audit log (flag changes)",
            cols: AUDIT_COLS,
            file: "audit",
            loading: auditLoading,
            csv: handle(fetchAudit, setAuditLoading, "waveFlagHistory", AUDIT_COLS, "audit", "csv", { waveId, limit: 10000 }),
            json: handle(fetchAudit, setAuditLoading, "waveFlagHistory", AUDIT_COLS, "audit", "json", { waveId, limit: 10000 }),
        },
        {
            label: "Sessions",
            cols: SESSION_COLS,
            file: "sessions",
            loading: sessionsLoading,
            csv: handle(fetchSessions, setSessionsLoading, "waveSessions", SESSION_COLS, "sessions", "csv", { waveId, limit: 10000 }),
            json: handle(fetchSessions, setSessionsLoading, "waveSessions", SESSION_COLS, "sessions", "json", { waveId, limit: 10000 }),
        },
        {
            label: "Participant arms",
            cols: ARM_COLS,
            file: "arms",
            loading: armsLoading,
            csv: handle(fetchArms, setArmsLoading, "waveAllParticipantArms", ARM_COLS, "arms", "csv", { waveId }),
            json: handle(fetchArms, setArmsLoading, "waveAllParticipantArms", ARM_COLS, "arms", "json", { waveId }),
        },
    ];

    return (
        <div className="admin-section">
            <h3>Export data</h3>
            <p style={{ color: "#666", marginBottom: 16, fontSize: 14 }}>
                Downloads are scoped to wave #{waveId}. Large exports may take a moment.
            </p>
            {tables.map(t => (
                <div key={t.file} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid #eee" }}>
                    <span style={{ flex: 1 }}>
                        <strong>{t.label}</strong>
                        <span style={{ color: "#888", fontSize: 12, marginLeft: 8 }}>{t.cols.join(", ")}</span>
                    </span>
                    <button className="admin-btn" disabled={t.loading} onClick={t.csv}>
                        {t.loading ? "…" : "↓ CSV"}
                    </button>
                    <button className="admin-btn" disabled={t.loading} onClick={t.json}>
                        {t.loading ? "…" : "↓ JSON"}
                    </button>
                </div>
            ))}
        </div>
    );
}
